import { ComponentProps, JSX, ValidComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import c from "class-c";
import { eventHandler } from "solid-u";

import createPropsProvider from "@repo/utils-solid/createPropsProvider";

import Identity from "../flow/Identity";

import styles from "./ContainerButton.module.scss";

declare namespace ContainerButton {
  type Props = Omit<ComponentProps<"div">, "style"> & {
    disabled?: boolean;
    style?: JSX.CSSProperties;
    as?: ValidComponent;
  };
}

function ContainerButton(_props: D<ContainerButton.Props>) {
  const { style, children, as, ...props } =
    ContainerButton.PropsProvider.useMerge(_props) satisfies D;

  const onClick = () => (props.disabled ? undefined : props.onClick);

  // role="button" doesn't give keyboard activation, native buttons and links
  // already have it
  const onKeyDown = (
    e: KeyboardEvent & { currentTarget: HTMLDivElement; target: Element }
  ) => {
    eventHandler(props.onKeyDown)(e);
    if (e.defaultPrevented || e.target !== e.currentTarget) return;
    if (e.currentTarget instanceof HTMLButtonElement) return;
    if (e.currentTarget instanceof HTMLAnchorElement) return;
    if (props.disabled || (e.key !== "Enter" && e.key !== " ")) return;

    // Still cancel repeats so a held Space doesn't scroll the page
    e.preventDefault();
    if (!e.repeat) e.currentTarget.click();
  };

  return (
    <Dynamic
      component={as || "div"}
      class={c`${styles["container-button"]} ${props.class}`}
      style={{
        cursor: onClick() ? "pointer" : undefined,
        ...style,
      }}
      tabIndex={0}
      role="button"
      {...props}
      onKeyDown={onKeyDown}
    >
      {children}
    </Dynamic>
  );
}

declare namespace ContainerButton.If {
  type Props = ContainerButton.Props & {
    when: unknown;
  };
}

ContainerButton.If = ({
  when,
  children,
  ...props
}: D<ContainerButton.If.Props>) => {
  return (
    <Dynamic component={when ? ContainerButton : Identity} {...props}>
      {children}
    </Dynamic>
  );
};

ContainerButton.PropsProvider =
  createPropsProvider<ContainerButton.Props>("ContainerButton");

export default ContainerButton;
